/**
 * <EmptyState> — empty-state placeholder with REQUIRED copywriting props.
 *
 * Per UI-SPEC §Copywriting Contract:
 *   - Heading default: `No {entities} yet` (English) / `لا توجد {entities} بعد` (Arabic)
 *   - Body default:    `Once you {nextAction}, it'll show up here.` (English)
 *
 * Both `entities` and `nextAction` are REQUIRED `NonEmptyString` props — no
 * hardcoded fallback, no generic "Nothing here" copy. This enforces the
 * "consumer must supply an entity-aware label" rule at COMPILE time.
 *
 * ── v2 (WS-2, additive) ──────────────────────────────────────────────────
 * Three OPTIONAL props extend the primitive without changing any existing
 * call-site's rendering:
 *   - `variant`      "first-use" | "filtered" | "error" | "done" — sets the
 *                    semantic tone (and the default illustration for the
 *                    `done`/`filtered` cases). Omitted ⇒ neutral (today's look).
 *   - `illustration` named key into the built-in geometric SVG set. Overrides
 *                    the variant's default illustration. Omitted + no `icon` +
 *                    no variant-default ⇒ nothing rendered above the heading
 *                    (byte-identical to the pre-v2 no-icon case).
 *   - `compact`      tighter paddings + smaller art for in-card / in-panel use.
 *
 * Back-compat: with none of the v2 props supplied the output is exactly the
 * pre-v2 markup (sunken card, `p-xl`, optional `icon`, heading, body, cta).
 *
 * Usage:
 *   <EmptyState
 *     entities={nonEmpty("employees")}
 *     nextAction={nonEmpty("add an employee")}
 *     variant="first-use"
 *     illustration="people"
 *     cta={<Button>Add employee</Button>}
 *   />
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils";
import type { NonEmptyString } from "../../types/NonEmptyString";
import { ILLUSTRATIONS, type IllustrationName } from "./illustrations";

/** Semantic intent of the empty state — drives tone + default illustration. */
export type EmptyStateVariant = "first-use" | "filtered" | "error" | "done";

/**
 * Per-variant defaults: the base tone (a `text-*` token class the illustration
 * inherits via `currentColor`) and the fallback illustration when the consumer
 * doesn't name one. `undefined` illustration ⇒ no art unless `illustration`/
 * `icon` is passed, so `first-use`/`error` stay opt-in for imagery.
 */
const VARIANT_META: Record<
  EmptyStateVariant,
  { tone: string; illustration: IllustrationName | undefined }
> = {
  "first-use": { tone: "text-primary", illustration: undefined },
  filtered: { tone: "text-muted", illustration: "search" },
  error: { tone: "text-danger", illustration: undefined },
  done: { tone: "text-success", illustration: "inbox-zero" },
};

export interface EmptyStateProps {
  /**
   * REQUIRED — plural entity noun used in the heading template
   * `No {entities} yet` (e.g. "employees", "leave requests", "payslips").
   * Must be a `NonEmptyString` — empty strings fail to typecheck.
   */
  entities: NonEmptyString;
  /**
   * REQUIRED — verb phrase used in the body template
   * `Once you {nextAction}, it'll show up here.` (e.g. "add an employee",
   * "submit a leave request"). Must be a `NonEmptyString`.
   */
  nextAction: NonEmptyString;
  /**
   * Optional override for the full heading string — use when the consumer
   * wants a non-template heading. When supplied, `entities` is not
   * substituted into a template but is still present on the type for the
   * axe/testing contract (a11y labels, analytics tags).
   */
  heading?: React.ReactNode;
  /**
   * Optional override for the full body string — same pattern as `heading`.
   */
  body?: React.ReactNode;
  /** Optional CTA element — typically a `<Button>` that triggers the next action. */
  cta?: React.ReactNode;
  /**
   * Optional secondary action, rendered under the CTA — typically a subtle
   * "Learn more" link. Kept visually quieter than `cta`.
   */
  secondaryCta?: React.ReactNode;
  /**
   * Optional illustration/icon shown above the heading. Takes precedence over
   * the built-in `illustration` set when both are supplied (a consumer can
   * still pass a bespoke Lucide icon).
   */
  icon?: React.ReactNode;
  /**
   * v2 — semantic intent. Sets the tone token the illustration inherits and,
   * for `done`/`filtered`, a sensible default illustration. Omit for the
   * neutral (pre-v2) look.
   */
  variant?: EmptyStateVariant;
  /**
   * v2 — named built-in illustration. Overrides the variant default. Ignored
   * when `icon` is supplied (bespoke icon wins).
   */
  illustration?: IllustrationName;
  /**
   * v2 — tighter paddings + smaller art for in-card / in-panel placement.
   * Defaults to `false` (full-bleed page-level spacing).
   */
  compact?: boolean;
  /** Consumer className forwarded to the outer container. */
  className?: string;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  {
    entities,
    nextAction,
    heading,
    body,
    cta,
    secondaryCta,
    icon,
    variant,
    illustration,
    compact = false,
    className,
  },
  ref,
) {
  const { t } = useTranslation("common");
  const resolvedHeading = heading ?? t("empty.heading", { entities });
  const resolvedBody = body ?? t("empty.body", { next_action: nextAction });

  const meta = variant !== undefined ? VARIANT_META[variant] : undefined;
  // Illustration resolution: explicit `illustration` prop wins, else the
  // variant's default. `icon` (bespoke) short-circuits the built-in art.
  const illustrationName = illustration ?? meta?.illustration;
  const Illustration =
    icon === undefined && illustrationName !== undefined
      ? ILLUSTRATIONS[illustrationName]
      : undefined;
  // Tone class the illustration inherits via currentColor. Bespoke `icon`
  // keeps the historical `text-muted` so untouched call-sites don't shift.
  const toneClass = meta?.tone ?? "text-muted";

  return (
    <div
      ref={ref}
      className={cn(
        // Layout — centered column, generous padding
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-sm p-lg" : "gap-md p-xl",
        // Surface — sunken tint so the empty-state reads as "below" the
        // surrounding content rather than competing with it.
        "rounded-lg bg-surface-sunken",
        className,
      )}
    >
      {Illustration !== undefined ? (
        <div className={cn(toneClass, "mb-xs")} aria-hidden="true">
          <Illustration className={compact ? "w-24" : "w-40 max-w-[160px]"} />
        </div>
      ) : (
        icon !== undefined && (
          <div className={toneClass} aria-hidden="true">
            {icon}
          </div>
        )
      )}
      <h2 className={compact ? "text-heading" : "text-display"}>{resolvedHeading}</h2>
      <p className="text-body text-muted max-w-md">{resolvedBody}</p>
      {cta !== undefined && <div className="mt-sm">{cta}</div>}
      {secondaryCta !== undefined && <div className="text-label text-muted">{secondaryCta}</div>}
    </div>
  );
});
